import { defaultApi } from '@/plugins/api/defaultApi'
import kakaoPlugin from '@/plugins/kakao'
import { camelCaseKeys, sleep } from '@/util'
import jwtDecode from 'jwt-decode'
import env from './env'
import $cookies from 'vue-cookies'

const TOKEN_NAME = env.state.token.name
const REFRESH_TOKEN_NAME = env.state.token.refreshTokenName

const getToken = () => {
  if (localStorage.getItem(TOKEN_NAME) === 'undefined') {
    localStorage.clear()
  }

  return {
    accessToken: localStorage.getItem(TOKEN_NAME),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_NAME),
  }
}

let refreshLoading = false
const resolves = [] // 토큰 갱신시 블럭걸린 resolve

let meLoading = false
let signinRefreshLoading = false
let syncMeLoading = false

const meLoadingCheck = async () => {
  if (meLoading === false) {
    return true
  }
  await sleep(10)
  return meLoadingCheck()
}
const st = {
  name: 'auth',
  namespaced: true,
  state: {
    info: null,
    refresh: null,
    me: null,
    meRefreshKey: null, // me refresh용도로 사용하는 키
    token: null,
    switchableAccounts: null,
  },
  getters: {
    isNiceCheckApp(state) {
      return !state?.me?.acquisitionRoute
    },
    registerWooriCrowd(state) {
      return state?.me?.acquisitionRoute?.name?.includes('woori-crowd')
    },
    // 회원가입 분리?
    referralCode(state) {
      return state?.me?.agent?.code
    },
    getTokenExpireTime(state) {
      if (state.info == null) {
        return null
      }
      const t = state.info.exp
      const d = new Date(t)
      // minusMin
      d.setMinutes(d.getMinutes() - 20) // 현재 토큰이 1시간짜리라서 40분짜리 토큰으로 만든다. 20분 빼기
      return d.getTime()
    },
    getRefreshTokenExpireTime(state) {
      if (state.info == null) {
        return null
      }
      return state.info.exp
    },
    hasAnyRole: (state) => (roles) => {
      const authorities = state?.info?.authorities
      if (!state.info.authorities) {
        return false
      }
      return !!authorities.filter((obj) => {
        return roles.indexOf(obj) >= 0
      }).length
    },
    isAuthenticated(state) {
      return state.info != null
    },
    isServiceSetUse(state) {
      const serviceSet = state?.me?.serviceSet
      if (!serviceSet) {
        return false
      }
      return serviceSet.find((s) => s.code === 'early_payments') ? true : false
    },
  },
  mutations: {
    signinSuccess(state, token) {
      localStorage[TOKEN_NAME] = token.accessToken
      localStorage[REFRESH_TOKEN_NAME] = token.refreshToken

      const sec = 1000
      state.token = token.accessToken
      state.info = camelCaseKeys(jwtDecode(token.accessToken))
      state.info.exp = parseInt(state.info.exp) * sec
      state.refresh = state.info
      state.refresh.exp = state.info.exp
    },
    signout(state) {
      delete localStorage[TOKEN_NAME]
      delete localStorage[REFRESH_TOKEN_NAME]

      for (const k in state) {
        state[k] = null
      }
    },
    setMe(state, me) {
      state.me = me
      state.meRefreshKey = `mr-${Date.now()}`
    },
    setSwitchableAccounts(state, accounts) {
      state.switchableAccounts = accounts
    },
  },
  actions: {
    isTokenExpire({ getters }) {
      const exp = getters.getTokenExpireTime
      if (!exp) {
        return false
      }
      return exp < Date.now()
      // return true
    },
    isRefreshTokenExpire({ getters }) {
      const exp = getters.getRefreshTokenExpireTime
      if (!exp) {
        return false
      }
      return exp < Date.now()
    },
    signinSuccess({ commit }, token) {
      commit('signinSuccess', token)
      return token
    },
    async signin({ dispatch }, user) {
      const { data } = await defaultApi.signin(user.id, user.password)
      await dispatch('signinSuccess', Object.assign(getToken(), data))
      return data
    },
    async getMe({ state, commit, getters }) {
      if (getters.isAuthenticated === false) {
        return
      } else if (meLoading === true) {
        await meLoadingCheck()
        return state.me
      }
      try {
        meLoading = true
        const { data } = await defaultApi.getMe()
        commit('setMe', data)
        return data
      } finally {
        meLoading = false
      }
    },
    async init({ dispatch, getters, commit }) {
      if (!getters) {
        return
      }
      if (getToken().refreshToken != null) {
        await dispatch('signinSuccess', getToken())
        await dispatch('sync')
      }
      return getters['isAuthenticated']
    },
    async signout({ commit }) {
      commit('signout')
    },
    async refresh({ dispatch }) {
      if (getToken().refreshToken && signinRefreshLoading === false) {
        try {
          signinRefreshLoading = true
          const { data } = await defaultApi.signinRefresh({
            token: getToken().refreshToken,
          })
          await dispatch(
            'signinSuccess',
            Object.assign(getToken(), {
              accessToken: data.token,
              refreshToken: data.token,
            }),
          )
          return data
        } catch (e) {
          if (e.status === 401) {
            await dispatch('signout')
          }
          throw e
        } finally {
          signinRefreshLoading = false
        }
      }
    },
    async sync({ dispatch }) {
      return new Promise(async (resolve, reject) => {
        if (resolves.length > 0 || refreshLoading) {
          resolves.push(resolve)
        } else if (refreshLoading === false && (await dispatch('isTokenExpire'))) {
          refreshLoading = true
          resolves.push(resolve)
          try {
            if (await dispatch('isRefreshTokenExpire')) {
              await dispatch('signout')
            } else {
              await dispatch('refresh')
            }
          } catch (e) {
            await dispatch('signout')
            reject(e)
          } finally {
            while (resolves.length > 0) {
              const r = resolves.pop()
              r(getToken())
            }
            refreshLoading = false
          }
        } else {
          resolve(getToken().token)
        }
      })
    },
    async updateMe({ state, dispatch }, me) {
      if (!state.info) {
        return
      }
      const { data } = await defaultApi.patchMe({ uuid: state.info.uuid, me })
      if (data.token) {
        await dispatch(
          'signinSuccess',
          Object.assign(getToken(), {
            accessToken: data.token,
            refreshToken: data.token,
          }),
        )
      }
      return data
    },
    async updateServiceUse({}) {
      const { data } = await defaultApi.postServicesCodeUse('early_payments')
      return data
    },
    async addIdPasswordUser({ dispatch }, user) {
      const { data } = await defaultApi.postUsers(user)
      await dispatch(
        'signinSuccess',
        Object.assign(getToken(), {
          accessToken: data.token,
          refreshToken: data.token,
        }),
      )
      return data
    },
    async addSimpleUser({ dispatch }, { businessLicenseNumber, referralCode }) {
      const { data } = await defaultApi.postSimpleUser({ businessLicenseNumber, referralCode })
      await dispatch(
        'signinSuccess',
        Object.assign(getToken(), {
          accessToken: data.token,
          refreshToken: data.token,
        }),
      )
      return data
    },
    async syncMe({ dispatch, getters }) {
      if (syncMeLoading === true) {
        return
      }
      try {
        syncMeLoading = true
        const me = await dispatch('getMe')
        let refreshFlag = false
        if (me.isNiceUser !== true) {
          await dispatch('updateMe', {
            isNiceUser: true,
            isDomestic: true,
            image: null,
          })
          refreshFlag = true
        }
        const couponText = $cookies.get('coupon')
        if (!!couponText) {
          await dispatch('updateMe', { couponText })
          $cookies.remove('coupon')
        }

        if (getters['isServiceSetUse'] === false) {
          await dispatch('updateServiceUse')
          refreshFlag = true
        }

        await dispatch('signupAcquisitionRoute')

        if (refreshFlag === true) {
          await dispatch('syncMe')
        }
      } finally {
        syncMeLoading = false
      }
    },
    async getSwitchableAccounts({ commit }) {
      const { data } = await defaultApi.getUsersSwitchable()
      commit('setSwitchableAccounts', data)
      return data
    },
    async signinKakao({ dispatch }, { redirectUri = '/' }) {
      // redirectUri가 null이면 /로 보낸다. string이 이상하게 들어오는 현상이어서 어쩔수없이 변경하게 해놓음..
      if (['null', 'undefined', null, undefined].includes(redirectUri)) {
        redirectUri = '/'
      }

      const url = `${location.origin}/callback/kakao/token?redirectUri=${encodeURIComponent(
        redirectUri,
      )}`
      const href = `${
        process.env.common.defaultApi.host
      }/api/auth/kakao/authorize/?next=${encodeURIComponent(url)}`
      location.href = href
    },
    async _kakaoSignin() {
      await kakaoPlugin.init()
      return new Promise(async (resolve, reject) => {
        await kakaoPlugin.init()
        Kakao.Auth.login({
          success: resolve,
          fail: reject,
        })
      })
    },
    async signinKakaoCode({ dispatch }, param) {
      const { data } = await defaultApi.signinKakao(param)
      await dispatch(
        'signinSuccess',
        Object.assign(getToken(), {
          accessToken: data.token,
          refreshToken: data.token,
        }),
      )
      await dispatch('getMe')
      return data
    },
    async signupAcquisitionRoute({ dispatch, state }) {
      const acquisitionRouteName = 'acquisitionRoute'
      const referralCodeName = 'referralCode'
      const acquisitionRoute = $cookies.get(acquisitionRouteName)
      const referralCode = $cookies.get(referralCodeName)
      if (
        (acquisitionRoute && !state?.me?.acquisitionRoute) ||
        (referralCode && !state?.me?.agent)
      ) {
        try {
          await dispatch('updateMe', {
            // ...state.me,
            referralCode,
            acquisitionRoute: {
              name: acquisitionRoute,
            },
          })
        } catch (e) {
          if (e.status === 400 && e.message === '존재하지 않습니다.') {
            //pass
          } else {
            throw e
          }
        }
      }
      $cookies.remove(acquisitionRouteName)
      $cookies.remove(referralCodeName)
    },
    async signinIdPassword({ dispatch }, { email, password }) {
      const { data } = await defaultApi.signinIdPassword({ email, password })
      await dispatch('signinSuccess', {
        accessToken: data.token,
        refreshToken: data.token,
      })
      return data
    },
    async resetPassword({}, params) {
      const { data } = await defaultApi.postUsersPasswordReset(params)
      return data
    },
    async earlyPaymentLoanRequest(_, params) {
      try {
        const requestData = {
          business_type: params.business_type,
          region: params.region,
          monthly_sales_amount: params.monthly_sales_amount,
          interested_services: params.interested_services,
          name: params.name,
          store_name: params.store_name,
          phone: params.phone
        }
        
        const response = await defaultApi.earlyPaymentLoanRequest(requestData)
        return response
      } catch (error) {
        console.error('조기상환 대출 요청 실패:', error)
        throw error
      }
    },
  },
}

export default st
